import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Image, Input, Textarea, VStack, HStack, Text, IconButton, useToast, Spinner, Container, Heading, Flex } from '@chakra-ui/react';
import { DeleteIcon, EditIcon, CheckIcon } from '@chakra-ui/icons';
import { getLikeImages, uploadLikeImage, updateLikeImageMemo, deleteLikeImage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useDisclosure } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
} from '@chakra-ui/react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);


const LikeImagesGallery = () => {
  const { currentUser } = useAuth();
  const { targetUserId } = useParams();
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editMemo, setEditMemo] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const toast = useToast();
  const fileInputRef = useRef();
  const [modalImage, setModalImage] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchImages = async () => {
    setLoading(true);
    try {
      const res = await getLikeImages(targetUserId || currentUser?.uid);
      setImages(res);
    } catch (e) {
      toast({ title: '画像一覧取得失敗', status: 'error' });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (currentUser?.uid) fetchImages();
    // eslint-disable-next-line
  }, [currentUser]);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      await uploadLikeImage(selectedFile, '', targetUserId || currentUser?.uid);
      toast({ title: 'アップロード成功', status: 'success' });
      setSelectedFile(null);
      fileInputRef.current.value = '';
      fetchImages();
    } catch (e) {
      toast({ title: 'アップロード失敗', status: 'error' });
    }
    setUploading(false);
  };

  const handleEdit = (id, memo) => {
    setEditId(id);
    setEditMemo(memo);
  };

  const handleSaveMemo = async (id) => {
    try {
      await updateLikeImageMemo(id, editMemo, targetUserId || currentUser?.uid);
      toast({ title: 'メモ更新', status: 'success' });
      setEditId(null);
      setEditMemo('');
      fetchImages();
    } catch (e) {
      toast({ title: 'メモ更新失敗', status: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('本当に削除しますか？')) return;
    try {
      await deleteLikeImage(id, targetUserId || currentUser?.uid);
      toast({ title: '削除しました', status: 'success' });
      fetchImages();
    } catch (e) {
      toast({ title: '削除失敗', status: 'error' });
    }
  };

  return (
    <Box w="100%">
      <Flex mb={6} justify="center" align="center" gap={4} flexWrap="wrap">
        <Input type="file" accept="image/*" onChange={handleFileChange} ref={fileInputRef} w="auto" />
        <Button onClick={handleUpload} isLoading={uploading} bg="black" color="white">アップロード</Button>
      </Flex>
      {loading ? <Spinner /> : (
        <VStack align="stretch" spacing={4} w="100%">
          {images.length === 0 && <Text>画像はありません</Text>}
          {images.map(img => (
            <Box key={img.id} borderWidth={1} borderRadius="md" p={2} boxShadow="sm">
              <HStack align="start" flexWrap={{ base: "wrap", md: "nowrap" }}>
                <Image src={img.image_url} alt="like" boxSize="120px" objectFit="contain" borderRadius="md" cursor="pointer" onClick={() => { setModalImage(img.image_url); onOpen(); }} />
                <VStack align="start" flex={1} spacing={1} w={{ base: "100%", md: "auto" }} mt={{ base: 2, md: 0 }}>
                  {editId === img.id ? (
                    <HStack w="100%">
                      <Textarea value={editMemo} onChange={e => setEditMemo(e.target.value)} size="sm" flex={1} />
                      <IconButton icon={<CheckIcon />} onClick={() => handleSaveMemo(img.id)} aria-label="保存" bg="black" color="white" />
                    </HStack>
                  ) : (
                    <HStack w="100%">
                      <Text whiteSpace="pre-wrap" flex={1}>{img.memo}</Text>
                      <IconButton icon={<EditIcon />} onClick={() => handleEdit(img.id, img.memo)} aria-label="編集" size="sm" />
                    </HStack>
                  )}
                  <HStack>
                    <IconButton icon={<DeleteIcon />} onClick={() => handleDelete(img.id)} aria-label="削除" size="sm" colorScheme="red" />
                  </HStack>
                </VStack>
              </HStack>
            </Box>
          ))}
        </VStack>
      )}
      <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none" w="100vw" h="100vh" m={0}>
          <ModalCloseButton 
            color="white" 
            bg="blackAlpha.600" 
            _hover={{ bg: "blackAlpha.800" }}
            size={{ base: "md", md: "lg" }}
            zIndex={20}
            position="fixed"
            top={{ base: 2, md: 4 }}
            right={{ base: 2, md: 4 }}
          />
          <ModalBody p={0} display="flex" alignItems="center" justifyContent="center" w="100%" h="100%">
            {modalImage && (
              <TransformWrapper
                initialScale={1}
                minScale={0.5}
                maxScale={4}
                centerOnInit={true}
                limitToBounds={false}
                smooth={true}
                wheel={{
                  step: 0.1,
                  wheelEnabled: true,
                  touchPadEnabled: true,
                }}
                pinch={{
                  step: 1,
                  disabled: false,
                }}
                doubleClick={{
                  step: 2,
                  mode: "toggle",
                }}
                onTransformed={(state) => {
                  // ズーム状態を追跡
                }}
              >
                {({ zoomIn, zoomOut, resetTransform, state }) => (
                  <TransformComponent
                    wrapperStyle={{
                      width: "100%",
                      height: "100vh",
                      maxWidth: "100%",
                      maxHeight: "100%",
                      touchAction: "none",
                    }}
                    contentStyle={{
                      width: "100%",
                      height: "100%",
                      touchAction: "none",
                    }}
                  >
                    <Box position="relative" w="100%" h="100%">
                      <Image
                        src={modalImage}
                        alt="拡大画像"
                        w="100%"
                        h="100vh"
                        objectFit="contain"
                        draggable={false}
                        userSelect="none"
                        style={{
                          touchAction: "none",
                          WebkitTouchCallout: "none",
                          WebkitUserSelect: "none",
                          KhtmlUserSelect: "none",
                          MozUserSelect: "none",
                          msUserSelect: "none",
                          userSelect: "none",
                        }}
                      />
                      {/* ズームインジケーター */}
                      {state && state.scale !== 1 && (
                        <Box
                          position="absolute"
                          top={4}
                          left="50%"
                          transform="translateX(-50%)"
                          bg="blackAlpha.600"
                          color="white"
                          px={3}
                          py={1}
                          borderRadius="md"
                          fontSize="sm"
                          zIndex={10}
                        >
                          {Math.round(state.scale * 100)}%
                        </Box>
                      )}
                      {/* ズームコントロール */}
                      <HStack
                        position="absolute"
                        bottom={4}
                        left="50%"
                        transform="translateX(-50%)"
                        spacing={2}
                        zIndex={10}
                      >
                        <IconButton
                          icon={<Text fontSize="sm">+</Text>}
                          onClick={() => zoomIn()}
                          bg="blackAlpha.600"
                          color="white"
                          _hover={{ bg: "blackAlpha.800" }}
                          size="sm"
                          aria-label="ズームイン"
                        />
                        <IconButton
                          icon={<Text fontSize="sm">-</Text>}
                          onClick={() => zoomOut()}
                          bg="blackAlpha.600"
                          color="white"
                          _hover={{ bg: "blackAlpha.800" }}
                          size="sm"
                          aria-label="ズームアウト"
                        />
                        <IconButton
                          icon={<Text fontSize="sm">⟲</Text>}
                          onClick={() => resetTransform()}
                          bg="blackAlpha.600"
                          color="white"
                          _hover={{ bg: "blackAlpha.800" }}
                          size="sm"
                          aria-label="リセット"
                        />
                      </HStack>
                    </Box>
                  </TransformComponent>
                )}
              </TransformWrapper>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default LikeImagesGallery; 